import { collection } from '@/infra/collection'
import type { CursorPaged } from '@/infra/paging'
import { Id } from '@/infra/id'
import type { SearchableUserResource } from '@/features/iam/api/resources/searchable-user'
import { type MembershipResource } from '@/features/iam/api/resources/workspaces'
import { SearchableUser, type SearchableUserProps } from './SearchableUser'
import type { UserDataType } from './UserData'
import {
  mapUserDataTypeResourceToUserDataType,
  mapUserDataTypeToResource,
} from './user-data-mappers'
import { type Membership } from '@/features/iam/workspaces/Membership'
import { mapMembershipResourceToMembership } from '@/features/iam/workspaces/workspace-mappers'
import { maybeParseDate } from '@/utils/date-util'
import { type IamApi } from '@/features/iam/api/iam-api'
import { type PrimeFullMemberFn } from '@/features/iam/members/MemberStore'
import { type PrimeFullWorkspaceFn } from '@/features/iam/workspaces/WorkspaceStore'
import { defer, distinctUntilChanged } from 'rxjs'
import type { OnlineStatusForUserFn } from '@/features/messaging'

export interface SearchUsersQuery {
  searchText?: string
  workspaceId?: Id
  userTypes?: UserDataType[] | null
  inactive?: boolean | null
  roleIds?: Id[]
  /**
   * Filters the users by the assigned advisor. Requires `userTypes` to be
   * `['User']`.
   */
  advisorUserPublicId?: Id
  after?: string | null
  limit?: number
}

export interface FetchSearchableUserParams {
  memberships?: boolean
}

export class SearchableUserStore {
  readonly searchableUsers = collection({
    create: () => {
      // Deferred because the user's IDs are only set after it is created.
      const user: SearchableUser = new SearchableUser(
        defer(() => this._onlineStatusForUser(user.publicId)).pipe(
          distinctUntilChanged(),
        ),
      )
      return user
    },
    getDataId: (data) => data.publicId,
    getModelId: (model) => model.publicId,
  })

  constructor(
    private readonly _api: IamApi,
    private readonly _primeFullMember: PrimeFullMemberFn,
    private readonly _primeFullWorkspace: PrimeFullWorkspaceFn,
    private readonly _onlineStatusForUser: OnlineStatusForUserFn,
  ) {}

  /**
   * Fetches a searchable user by public ID.
   */
  async fetchUser(
    publicId: Id,
    params: FetchSearchableUserParams,
    signal?: AbortSignal,
  ): Promise<SearchableUser> {
    const user = await this._api.userSearch.get(
      Id.assert(publicId),
      { memberships: params.memberships },
      signal,
    )

    return this.searchableUsers.set(
      this._mapSearchableUserResourceToProps(user),
    )
  }

  /**
   * Lists the users matching the query.
   */
  async listSearchableUsers(
    query: SearchUsersQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<SearchableUser>> {
    const response = await this._api.userSearch.list(
      {
        search_text: query.searchText,
        workspace_id: query.workspaceId && Id.assert(query.workspaceId),
        user_types: query.userTypes?.map(mapUserDataTypeToResource),
        inactive: query.inactive,
        role_ids: query.roleIds?.length
          ? query.roleIds.filter(Id.some)
          : undefined,
        advisor_user_public_id:
          query.advisorUserPublicId && Id.assert(query.advisorUserPublicId),
        after: query.after,
        limit: query.limit,
      },
      signal,
    )

    return {
      data: this.searchableUsers.set(
        response.data.map(this._mapSearchableUserResourceToProps),
      ),
      next: response.cursor,
      total: Number(response.total ?? 0),
    }
  }

  private readonly _mapSearchableUserResourceToProps = (
    resource: SearchableUserResource,
  ): Partial<SearchableUserProps> => ({
    userId: Id(resource.user_id),
    publicId: resource.public_id,
    userDataType: resource.user_data_type
      ? mapUserDataTypeResourceToUserDataType(resource.user_data_type)
      : 'User',
    givenName: resource.given_name,
    familyName: resource.family_name,
    email: resource.email,
    phone: resource.phone || '',
    picture: resource.picture,
    lastSeen: mapLastSeen(resource.last_seen),
    dateCreated: maybeParseDate(resource.date_created),
    dateModified: maybeParseDate(resource.date_modified),
    memberships: resource.memberships
      ? resource.memberships.map(this._mapMembershipResourceToMembership)
      : [],
    isWorkspaceCustomer: resource.is_workspace_customer ?? false,
  })

  private readonly _mapMembershipResourceToMembership = (
    resource: MembershipResource,
  ): Membership =>
    mapMembershipResourceToMembership(
      resource,
      this._primeFullWorkspace,
      this._primeFullMember,
    )
}

/**
 * Maps a user's last-seen timestamp, which the API sends as the Unix epoch for
 * users who have never signed in.
 */
function mapLastSeen(value: string | null | undefined): Date | null {
  const lastSeen = maybeParseDate(value)
  return lastSeen && lastSeen.getTime() > 0 ? lastSeen : null
}
